/**
 * statistics.ts
 *
 * This utility file provides statistical functions for numerical data analysis.
 *
 * See also:
 * - @../components/aggregation/aggregation.ts - Aggregation helpers (sum, min, avg, mode, deviation) for arrays/column values.
 */

/**
 * Returns the arithmetic mean of an array.
 * Returns NaN if the array is empty.
 *
 * @param xs - Array of numbers to calculate the mean of
 * @returns The arithmetic mean of the input array, or NaN if the array is empty
 * ! There is a `lodash` function called `mean` that might be identical and faster.
 */
export function calculateMean(data: number[]): number {
  return data.reduce((a, b) => a + b, 0) / data.length;
}

/**
 * Calculates both the mean and standard deviation of a dataset.
 *
 * @param xs - Array of numbers to calculate statistics for
 * @returns Object containing the mean and standard deviation
 */
export function meanAndStandardDeviation(xs: number[]): {
  mean: number;
  standardDeviation: number;
} {
  const M = calculateMean(xs);
  const standardDeviation = Math.sqrt(
    xs.map((x) => (x - M) ** 2).reduce((a, b) => a + b, 0) / xs.length
  );

  return { mean: M, standardDeviation };
}

/**
 * Calculates the sample variance of a dataset using Bessel's correction.
 * Sample variance is an unbiased estimator of the population variance.
 *
 * Caveats:
 * - If there is only one element in the array, the sample variance returns NaN.
 * - If the array is empty, the sample variance currently returns -0.
 *
 * @param data - Array of numbers to calculate the sample variance for
 * @returns The sample variance with Bessel's correction (n-1 denominator)
 * ! There is a `lodash` function called `variance` that might be identical and faster.
 */
export const calculateSampleVariance = (data: number[]): number => {
  const xBar = calculateMean(data);
  const n = data.length;
  return data.reduce((a, b) => a + (b - xBar) ** 2, 0) / (n - 1); // Bessel's correction is needed
};

/**
 * Calculates the quantile value from a dataset.
 *
 * @param data - Array of numbers to calculate the quantile from
 * @param q - The quantile to calculate (between 0 and 1)
 * @returns The value at the specified quantile
 */
export const quantile = (data: number[], q: number): number => {
  const n = data.length;
  const sorted = data.slice().sort((a, b) => a - b); // Sort numerically
  return sorted[Math.floor(n * q)];
};
